import * as Yup from "yup";

// Category Validation Schema
export const categoryValidationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .min(2, "Title must be at least 2 characters")
    .max(100, "Title must be less than 100 characters")
    .trim(),

  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description must be less than 500 characters")
    .trim(),

  image: Yup.mixed()
    .nullable()
    .test("fileSize", "File size must be less than 5MB", (value) => {
      if (!value) return true; // Allow null/undefined
      return value.size <= 5 * 1024 * 1024; // 5MB
    })
    .test("fileType", "Only image files are allowed", (value) => {
      if (!value) return true; // Allow null/undefined
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      return allowedTypes.includes(value.type);
    }),

  isActive: Yup.boolean().default(true),

  parentCategoryId: Yup.string()
    .nullable()
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid parent category ID format"),
});

// Product Validation Schema
export const productValidationSchema = Yup.object({
  title: Yup.string()
    .required("Title is required")
    .min(2, "Title must be at least 2 characters")
    .max(100, "Title must be less than 100 characters")
    .trim(),

  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must be less than 1000 characters")
    .trim(),

  categoryId: Yup.string()
    .required("Category is required")
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid category ID format"),

  isActive: Yup.boolean().default(true),

  isTrending: Yup.boolean().default(false),

  metaTitle: Yup.string()
    .nullable()
    .max(60, "Meta title must be less than 60 characters")
    .trim(),

  metaDescription: Yup.string()
    .nullable()
    .max(160, "Meta description must be less than 160 characters")
    .trim(),

  tags: Yup.array()
    .of(
      Yup.string()
        .trim()
        .max(50, "Each tag must be less than 50 characters")
    )
    .max(20, "Maximum 20 tags allowed")
    .default([]),
});

// Product Variant Validation Schema
export const productVariantValidationSchema = Yup.object({
  productId: Yup.string()
    .nullable()
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid product ID format"),

  attributes: Yup.array()
    .of(
      Yup.object({
        attributeName: Yup.string()
          .required("Attribute name is required")
          .min(1, "Attribute name cannot be empty")
          .max(50, "Attribute name must be less than 50 characters")
          .trim(),
        attributeType: Yup.string().required("Attribute type is required"),
        value: Yup.mixed().when("attributeType", {
          is: "multi-select",
          then: (schema) =>
            schema
              .nullable()
              .test(
                "is-array-or-null",
                "Value must be an array for multi-select attributes",
                (value) => value === null || Array.isArray(value)
              ),
          otherwise: (schema) =>
            Yup.mixed()
              .required("Attribute value is required")
              .test(
                "is-string-or-number",
                "Value must be a string or number",
                (value) =>
                  typeof value === "string" || typeof value === "number"
              ),
        }),
        values: Yup.array().when("attributeType", {
          is: (attributeType) => attributeType === "multi-select",
          then: (schema) =>
            schema
              .required("Attribute values are required")
              .min(1, "At least one value must be selected")
              .of(Yup.mixed().required("Value cannot be empty")),
          otherwise: (schema) => schema.notRequired(),
        }),
        colorHex: Yup.string().when("attributeType", {
          is: "color-picker",
          then: (schema) =>
            schema
              .required("Color selection is required")
              .matches(
                /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/,
                "Enter a valid HEX color (e.g., #FFAA33)"
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    )
    .min(1, "At least one attribute is required"),

  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be non-negative")
    .max(999999.99, "Price must be less than 999,999.99")
    .test("decimal", "Price can have maximum 2 decimal places", (value) => {
      if (!value) return true;
      return /^\d+(\.\d{1,2})?$/.test(value.toString());
    }),

  stock: Yup.number()
    .required("Stock is required")
    .integer("Stock must be a whole number")
    .min(0, "Stock must be non-negative")
    .max(999999, "Stock must be less than 999,999"),

  sku: Yup.string()
    .nullable()
    .optional()
    .max(50, "SKU must be less than 50 characters")
    .trim(),

  keyIngredients: Yup.array()
    .of(
      Yup.string()
        .min(1, "Ingredient cannot be empty")
        .max(100, "Ingredient must be less than 100 characters")
        .trim()
    )
    .max(20, "Maximum 20 ingredients allowed")
    .default([]),

  howToUse: Yup.array()
    .of(
      Yup.object({
        step: Yup.string()
          .min(1, "Step title cannot be empty")
          .max(100, "Step title must be less than 100 characters")
          .trim(),
        description: Yup.string()
          .min(1, "Step description cannot be empty")
          .max(500, "Step description must be less than 500 characters")
          .trim(),
      })
    )
    .max(10, "Maximum 10 steps allowed")
    .default([]),

  productCode: Yup.string()
    .nullable()
    .matches(/^\d{12}$/, "Product code must be exactly 12 digits")
    .trim(),

  images: Yup.array()
    .of(
      Yup.mixed()
        .test("fileSize", "File size must be less than 10MB", (value) => {
          if (!value) return true;
          return value.size <= 10 * 1024 * 1024; // 5MB
        })
        .test("fileType", "Only image files are allowed", (value) => {
          if (!value) return true;
          const allowedTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/gif",
          ];
          return allowedTypes.includes(value.type);
        })
    )
    .max(5, "Maximum 5 images allowed"),

  existingImages: Yup.array().of(Yup.string()).nullable(),

  video: Yup.mixed()
    .nullable()
    .optional()
    .test("fileSize", "Video file size must be less than 100MB", (value) => {
      if (!value) return true;
      return value.size <= 100 * 1024 * 1024; // 100MB
    })
    .test("fileType", "Only video files are allowed", (value) => {
      if (!value) return true;
      const allowedTypes = [
        "video/mp4",
        "video/webm",
        "video/ogg",
        "video/avi",
        "video/mov",
      ];
      return allowedTypes.includes(value.type);
    }),

  existingVideo: Yup.string().nullable().optional(),

  isActive: Yup.boolean().default(true),

  isDefault: Yup.boolean().default(false),
});

// Stock Update Validation Schema
export const stockUpdateValidationSchema = Yup.object({
  operation: Yup.string()
    .required("Operation is required")
    .oneOf(
      ["set", "add", "subtract"],
      "Operation must be set, add, or subtract"
    ),

  stock: Yup.number()
    .required("Stock amount is required")
    .integer("Stock must be a whole number")
    .min(0, "Stock must be non-negative")
    .max(999999, "Stock must be less than 999,999"),
});

// User Validation Schema
export const userValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .trim(),

  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format")
    .max(100, "Email must be less than 100 characters")
    .lowercase()
    .trim(),

  password: Yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(100, "Password must be less than 100 characters"),

  role: Yup.string()
    .required("Role is required")
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid role ID format"),

  status: Yup.string()
    .oneOf(["active", "inactive"], "Status must be active or inactive")
    .default("active"),
});

// Review Validation Schema
export const reviewValidationSchema = Yup.object({
  productId: Yup.string()
    .required("Product ID is required")
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid product ID format"),

  userId: Yup.string()
    .required("User ID is required")
    .matches(/^[0-9a-fA-F]{24}$/, "Invalid user ID format"),

  rating: Yup.number()
    .required("Rating is required")
    .integer("Rating must be a whole number")
    .min(1, "Rating must be at least 1")
    .max(5, "Rating must be at most 5"),

  title: Yup.string()
    .required("Review title is required")
    .min(2, "Title must be at least 2 characters")
    .max(100, "Title must be less than 100 characters")
    .trim(),

  comment: Yup.string()
    .required("Review comment is required")
    .min(10, "Comment must be at least 10 characters")
    .max(1000, "Comment must be less than 1000 characters")
    .trim(),

  status: Yup.string()
    .oneOf(
      ["pending", "approved", "rejected"],
      "Status must be pending, approved, or rejected"
    )
    .default("pending"),
});

// Contact Validation Schema
export const contactValidationSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters")
    .trim(),

  email: Yup.string()
    .required("Email is required")
    .email("Invalid email format")
    .max(100, "Email must be less than 100 characters")
    .lowercase()
    .trim(),

  subject: Yup.string()
    .required("Subject is required")
    .min(2, "Subject must be at least 2 characters")
    .max(200, "Subject must be less than 200 characters")
    .trim(),

  message: Yup.string()
    .required("Message is required")
    .min(10, "Message must be at least 10 characters")
    .max(1000, "Message must be less than 1000 characters")
    .trim(),

  category: Yup.string()
    .oneOf(
      ["general", "support", "sales", "feedback", "complaint"],
      "Invalid category"
    )
    .default("general"),

  priority: Yup.string()
    .oneOf(["low", "medium", "high", "urgent"], "Invalid priority")
    .default("medium"),
});

// Ticket Validation Schema
export const ticketValidationSchema = Yup.object({
  customerName: Yup.string()
    .required("Customer name is required")
    .min(2, "Customer name must be at least 2 characters")
    .max(100, "Customer name must be less than 100 characters")
    .trim(),

  customerEmail: Yup.string()
    .required("Customer email is required")
    .email("Invalid email format")
    .max(100, "Email must be less than 100 characters")
    .lowercase()
    .trim(),

  subject: Yup.string()
    .required("Subject is required")
    .min(2, "Subject must be at least 2 characters")
    .max(200, "Subject must be less than 200 characters")
    .trim(),

  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(1000, "Description must be less than 1000 characters")
    .trim(),

  category: Yup.string()
    .oneOf(
      ["technical", "billing", "general", "feature-request"],
      "Invalid category"
    )
    .default("general"),

  priority: Yup.string()
    .oneOf(["low", "medium", "high", "urgent"], "Invalid priority")
    .default("medium"),

  status: Yup.string()
    .oneOf(["open", "in-progress", "resolved", "closed"], "Invalid status")
    .default("open"),
});

// Email Campaign Validation Schema
export const emailCampaignValidationSchema = Yup.object({
  name: Yup.string()
    .required("Campaign name is required")
    .min(2, "Campaign name must be at least 2 characters")
    .max(100, "Campaign name must be less than 100 characters")
    .trim(),

  subject: Yup.string()
    .required("Email subject is required")
    .min(2, "Subject must be at least 2 characters")
    .max(200, "Subject must be less than 200 characters")
    .trim(),

  content: Yup.string()
    .required("Email content is required")
    .min(10, "Content must be at least 10 characters")
    .max(10000, "Content must be less than 10,000 characters")
    .trim(),

  type: Yup.string()
    .oneOf(
      ["newsletter", "promotional", "announcement", "welcome"],
      "Invalid campaign type"
    )
    .default("newsletter"),

  status: Yup.string()
    .oneOf(
      ["draft", "scheduled", "sending", "sent", "paused", "cancelled"],
      "Invalid status"
    )
    .default("draft"),

  scheduledAt: Yup.date()
    .nullable()
    .min(new Date(), "Scheduled date must be in the future"),
});

// Export all validation schemas
const validationSchemas = {
  categoryValidationSchema,
  productValidationSchema,
  productVariantValidationSchema,
  stockUpdateValidationSchema,
  userValidationSchema,
  reviewValidationSchema,
  contactValidationSchema,
  ticketValidationSchema,
  emailCampaignValidationSchema,
};

export default validationSchemas;
